import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  Empty,
  Flex,
  Form,
  Modal,
  ModalProps,
  Row,
  Select,
  Spin
} from 'antd'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  useClassifierServiceGetInvestigationClassifierListEndpointKey,
  useInvestigationsServiceCreateSpecimenInvestigationEndpoint,
  useInvestigationsServiceGetListSpecimenInvestigationsEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  ClassifierService,
  InvestigationTypeItemDto
} from 'src/api/requests'
import { DEFAULT_PER_PAGE, requiredRules } from 'src/app-constants'
import { message, SaveConfirm } from 'src/components'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  /** The specimen type: only investigation types accepting it are offered. */
  specimenId?: string
  /** Investigation types already on the specimen; they are not offered again. */
  existingTypeIds?: string[]
  onCancel: () => void
} & ModalProps

// Flattens the API validation errors (field -> messages) into one text.
const errorText = (error: unknown) => {
  const apiError = error as ApiError
  const errors = apiError?.body?.errors

  const messages =
    errors && typeof errors === 'object'
      ? Object.values(errors as Record<string, unknown>)
          .flat()
          .filter((item): item is string => typeof item === 'string')
      : []

  return messages.join('. ') || apiError?.message || ''
}

export const AddInvestigationModal = ({
  specimenId,
  existingTypeIds = [],
  onCancel,
  ...props
}: Props) => {
  const { t, currentLang } = useLocales()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()
  const [isSaving, setIsSaving] = useState(false)

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, fetchNextPage, isFetching, hasNextPage } = useInfiniteQuery({
    queryKey: [
      useClassifierServiceGetInvestigationClassifierListEndpointKey,
      debouncedSearch,
      specimenId
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await ClassifierService.getInvestigationClassifierListEndpoint(
          pageParam,
          DEFAULT_PER_PAGE,
          debouncedSearch,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          [specimenId || '']
        )

      return {
        data: response.data ?? [],
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: InvestigationTypeItemDto[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const existing = new Set(existingTypeIds)

  const selectOptions = (
    data?.pages.flatMap(
      (page) =>
        page.data?.map((item: InvestigationTypeItemDto) => ({
          value: item?.id,
          label: item?.title?.[currentLang?.value]
        })) || []
    ) || []
  ).filter((option) => !existing.has(String(option.value)))

  const { mutateAsync: addInvestigation } =
    useInvestigationsServiceCreateSpecimenInvestigationEndpoint()

  // One request per type, one after the other: the prefix is generated by the
  // backend from the types already on the specimen, so parallel requests could
  // pick the same one. Added types leave the selection; failed ones stay.
  const onSubmit = async () => {
    const { typeIds } = await form.validateFields()

    setIsSaving(true)

    const failed: string[] = []
    const errors: { type: string; error: string }[] = []

    for (const typeId of typeIds as string[]) {
      try {
        await addInvestigation({
          specimenId: String(id),
          requestBody: { typeId }
        })
      } catch (error) {
        failed.push(typeId)
        errors.push({
          type:
            String(
              selectOptions.find((option) => option.value === typeId)?.label ??
                ''
            ) || typeId,
          error: errorText(error)
        })
      }
    }

    setIsSaving(false)

    if (failed.length < typeIds.length) {
      queryClient.invalidateQueries({
        queryKey: [
          useInvestigationsServiceGetListSpecimenInvestigationsEndpointKey
        ]
      })
    }

    if (!failed.length) {
      message.success(
        t(
          typeIds.length > 1
            ? 'specimens.investigations_added'
            : 'specimens.investigation_added'
        )
      )
      onCancel()
      return
    }

    errors.forEach(({ type, error }) =>
      message.error(t('specimens.investigation_add_failed', { type, error }))
    )
    form.setFieldValue('typeIds', failed)
  }

  return (
    <Modal
      open
      title={t('specimens.add_investigation')}
      onCancel={onCancel}
      footer={null}
      maskClosable={false}
      {...props}
    >
      <Form form={form} requiredMark={false} layout="vertical">
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Form.Item
              name="typeIds"
              label={t('specimens.investigation_test_type')}
              rules={requiredRules}
            >
              <Select
                mode="multiple"
                allowClear
                popupMatchSelectWidth={true}
                filterOption={false}
                options={selectOptions}
                onSearch={(value) => setSearch(value)}
                onClear={() => setSearch('')}
                loading={isFetching}
                placeholder={t('specimens.investigation_test_type')}
                showSearch={true}
                onPopupScroll={(e) => {
                  const target = e.target as HTMLElement
                  if (
                    target.scrollTop + target.offsetHeight ===
                    target.scrollHeight
                  ) {
                    hasNextPage && fetchNextPage()
                  }
                }}
                notFoundContent={
                  isFetching ? (
                    <Flex justify="center">
                      <Spin />
                    </Flex>
                  ) : (
                    <Empty />
                  )
                }
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex justify="center" gap={16}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${t('common.save')}?`}
                onConfirm={onSubmit}
                okButtonProps={{ loading: isSaving }}
              >
                <Button type="primary" loading={isSaving}>
                  {t('common.save')}
                </Button>
              </SaveConfirm>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
